// skipcq: JS-W1028
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';

import { auth, db } from '../utils/firebase';

export const BREWERY_PERMISSIONS_COLLECTION = 'breweryPermissions';

/**
 * Permission rows paired with `breweries/{breweryId}` display fields for the manage portal.
 * @returns {Promise<Array<{
 *   id: string,
 *   breweryId: string,
 *   userId: string,
 *   role: string,
 *   breweryName: string,
 *   breweryCity: string,
 *   breweryState: string,
 *   breweryGroupId: string | null,
 * }>>}
 */
export async function fetchManageableBreweriesForCurrentUser() {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser) {
    return [];
  }

  const snapshot = await getDocs(
    query(
      collection(db, BREWERY_PERMISSIONS_COLLECTION),
      where('userId', '==', firebaseUser.uid),
    ),
  );

  const permissionRows = snapshot.docs.map((snapshotDoc) => ({
    id: snapshotDoc.id,
    ...snapshotDoc.data(),
  }));

  const enrichedRows = await Promise.all(
    permissionRows.map(async (permissionRow) => {
      const breweryId =
        typeof permissionRow.breweryId === 'string' ? permissionRow.breweryId : '';
      let breweryName = '';
      let breweryCity = '';
      let breweryState = '';
      let breweryGroupId = null;
      if (breweryId.length > 0) {
        const brewerySnap = await getDoc(doc(db, 'breweries', breweryId));
        if (brewerySnap.exists()) {
          const breweryData = brewerySnap.data();
          breweryName = typeof breweryData.name === 'string' ? breweryData.name : '';
          breweryCity = typeof breweryData.city === 'string' ? breweryData.city : '';
          breweryState = typeof breweryData.state === 'string' ? breweryData.state : '';
          const rawGroupId =
            typeof breweryData.breweryGroupId === 'string'
              ? breweryData.breweryGroupId.trim()
              : '';
          breweryGroupId = rawGroupId.length > 0 ? rawGroupId : null;
        }
      }
      const displayName = breweryName.length > 0 ? breweryName : breweryId;
      return {
        id: permissionRow.id,
        breweryId,
        userId:
          typeof permissionRow.userId === 'string' ? permissionRow.userId : firebaseUser.uid,
        role: typeof permissionRow.role === 'string' ? permissionRow.role : '',
        breweryName: displayName,
        breweryCity,
        breweryState,
        breweryGroupId,
      };
    }),
  );

  enrichedRows.sort((rowA, rowB) =>
    rowA.breweryName.localeCompare(rowB.breweryName, undefined, { sensitivity: 'base' }),
  );

  return enrichedRows;
}

/**
 * Permission rows for one brewery (global admin; Engage brewery detail).
 * @param {string} breweryId
 * @returns {Promise<Array<Record<string, unknown> & { id: string }>>}
 */
export async function fetchBreweryPermissionsForBrewery(breweryId) {
  if (typeof breweryId !== 'string' || breweryId.trim().length === 0) {
    return [];
  }
  const snapshot = await getDocs(
    query(
      collection(db, BREWERY_PERMISSIONS_COLLECTION),
      where('breweryId', '==', breweryId.trim()),
    ),
  );
  return snapshot.docs.map((snapshotDoc) => ({
    id: snapshotDoc.id,
    ...snapshotDoc.data(),
  }));
}
